import { MousePointerClick, MessageSquare, Network } from 'lucide-react';

/**
 * Progression diagram — three eras on one spine.
 * Past is recessive, present is filled accent, future is outlined accent.
 * Accent #6366f1 validated against the dark surface (#0f172a).
 */

const eras = [
  {
    tag: 'Yesterday',
    title: 'Click-Through UI',
    detail: 'Pages, menus, forms. The customer learns your navigation.',
    icon: MousePointerClick,
    state: 'past' as const,
  },
  {
    tag: 'Today',
    title: 'Conversation',
    detail: 'They say what they want. The service does it.',
    icon: MessageSquare,
    state: 'present' as const,
  },
  {
    tag: 'Tomorrow',
    title: 'MCP, A2A',
    detail: 'Their AI agent talks to your service directly. No human in the loop.',
    icon: Network,
    state: 'future' as const,
  },
];

const nodeStyles = {
  past: 'bg-slate-800 border-slate-600 text-slate-500',
  present: 'bg-indigo-500 border-indigo-400 text-white shadow-[0_0_24px_-4px_rgba(99,102,241,0.6)]',
  future: 'bg-slate-900 border-indigo-500 text-indigo-400',
};

const tagStyles = {
  past: 'text-slate-600',
  present: 'text-indigo-300',
  future: 'text-indigo-400/70',
};

const titleStyles = {
  past: 'text-slate-500',
  present: 'text-white',
  future: 'text-slate-200',
};

export function EraProgression() {
  return (
    <div className="relative">
      {/* spine — horizontal on desktop, vertical on mobile */}
      <div
        className="hidden sm:block absolute left-[16.666%] right-[16.666%] top-7 h-px"
        style={{
          background:
            'linear-gradient(to right, #475569 0%, #6366f1 50%, #6366f1 75%, rgba(99,102,241,0.25) 100%)',
        }}
        aria-hidden="true"
      />
      <div
        className="sm:hidden absolute left-7 top-7 bottom-7 w-px"
        style={{
          background:
            'linear-gradient(to bottom, #475569 0%, #6366f1 50%, rgba(99,102,241,0.25) 100%)',
        }}
        aria-hidden="true"
      />

      <ol className="relative grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-6 list-none p-0 m-0">
        {eras.map((era) => {
          const Icon = era.icon;
          return (
            <li key={era.tag} className="flex sm:flex-col items-start sm:items-center gap-4 sm:gap-0 sm:text-center">
              <div
                className={`w-14 h-14 rounded-full border-2 flex items-center justify-center shrink-0 sm:mb-4 transition-transform duration-200 hover:scale-105 ${nodeStyles[era.state]}`}
              >
                <Icon className="w-6 h-6" />
              </div>
              <div className="min-w-0">
                <p className={`text-[11px] uppercase tracking-wider mb-1 ${tagStyles[era.state]}`}>
                  {era.tag}
                </p>
                <h3 className={`text-base font-semibold tracking-tight mb-1.5 ${titleStyles[era.state]}`}>
                  {era.title}
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed sm:max-w-[16rem] sm:mx-auto">
                  {era.detail}
                </p>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
